import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Award, Users, ThumbsUp, Clock } from 'lucide-react';
import Container from '../components/layout/Container';
import SectionTitle from '../components/ui/SectionTitle';
import CTASection from '../components/ui/CTASection';

<Helmet>
  <title>About IDX.rainautomation.com | Premium IDX for Real Estate Agents</title>
  <meta name="description" content="Learn about our mission to deliver premium IDX tools and AI-driven websites to real estate professionals across the country." />
</Helmet>

const AboutPage: React.FC = () => {
  const values = [
    {
      icon: <Award size={28} />,
      title: 'Excellence',
      description: 'We strive for excellence in design, technology, and customer support.',
    },
    {
      icon: <Users size={28} />,
      title: 'Agent-Centric',
      description: 'Our platform is built around the real needs of modern real estate professionals.',
    },
    {
      icon: <ThumbsUp size={28} />,
      title: 'Reliability',
      description: 'You can count on our platform to perform consistently and securely.',
    },
    {
      icon: <Clock size={28} />,
      title: 'Innovation',
      description: 'We lead the market with AI-driven IDX tools and forward-thinking design.',
    },
  ];

  return (
    <>
      <section className="pt-32 pb-16">
        <Container>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h1 className="text-4xl md:text-5xl font-bold font-serif mb-6">
                About IDX.rainautomation.com
              </h1>
              <p className="text-xl text-neutral-700 mb-6">
                Elevating the digital presence of real estate agents through premium IDX and AI-powered websites.
              </p>
              <p className="text-neutral-600 mb-4">
                Founded in 2022 by industry experts, IDX.rainautomation.com was built to bridge the gap between cost-effective solutions and high-end functionality. Most IDX platforms were either affordable but barebones, or robust but overpriced. We’re here to change that.
              </p>
              <p className="text-neutral-600">
                Our mission is to deliver elegant, powerful, and accessible IDX websites that help agents win more listings, impress buyers, and grow their business online.
              </p>
            </motion.div>
            <motion.div
              className="relative"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="rounded-lg overflow-hidden shadow-premium">
                <img
                  src="https://images.pexels.com/photos/3182774/pexels-photo-3182774.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
                  alt="IDX.rainautomation.com team collaborating"
                  className="w-full h-auto"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-lg shadow-premium border border-neutral-200">
                <p className="font-semibold text-primary-600">Client Satisfaction</p>
                <p className="text-3xl font-bold">97%</p>
              </div>
            </motion.div>
          </div>
        </Container>
      </section>

      <section className="py-16 bg-neutral-50">
        <Container>
          <SectionTitle
            title="Our Core Values"
            subtitle="What drives every decision we make"
            centered
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                className="bg-white p-6 rounded-lg shadow-soft"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="text-primary-600 mb-4">{value.icon}</div>
                <h3 className="text-xl font-semibold mb-2">{value.title}</h3>
                <p className="text-neutral-600">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      <section className="py-16 bg-neutral-50">
        <Container>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
            <div className="md:col-span-1">
              <h2 className="text-3xl font-bold font-serif mb-4">Our Mission</h2>
              <p className="text-neutral-600">
                Empower agents and brokerages with modern IDX technology that helps them stand out, convert more leads, and streamline operations.
              </p>
            </div>
            <div className="md:col-span-1">
              <h2 className="text-3xl font-bold font-serif mb-4">Our Vision</h2>
              <p className="text-neutral-600">
                To become the #1 choice for IDX and real estate website solutions by combining AI, automation, and design excellence.
              </p>
            </div>
            <div className="md:col-span-1">
              <h2 className="text-3xl font-bold font-serif mb-4">Our Promise</h2>
              <p className="text-neutral-600">
                We listen, we build, and we adapt — so you can focus on growing your real estate business without technical headaches.
              </p>
            </div>
          </div>
        </Container>
      </section>

      <CTASection
        title="Join Our Growing Community"
        subtitle="Thousands of real estate professionals trust IDX.rainautomation.com to power their online presence"
        primaryButtonText="Get Started"
        secondaryButtonText="Contact Us"
        primaryButtonTo="/contact"
        secondaryButtonTo="/contact"
        background="gradient"
      />
    </>
  );
};

export default AboutPage;